
import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Loader2, Sparkles, Bot } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const WEBHOOK_URL = "https://app.thomastapella.it/webhook/ab0d11bd-6a4c-4d6a-bbb7-9f4f325d1841/chat";

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export const ChatBot: React.FC = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: "Ciao! Sono ChartTower BOT AI, il tuo assistente virtuale. Posso aiutarti con informazioni su Blue Strike, Algo Engine, il Token CTC, le prove gratuite e molto altro. Come posso esserti utile?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Session handling
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('ctc_chatbot_session');
    if (stored) return stored;
    const newId = crypto.randomUUID();
    localStorage.setItem('ctc_chatbot_session', newId);
    return newId;
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatInput: userMessage.text,
          sessionId: sessionId,
          timestamp: userMessage.timestamp.toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read text first to handle potential parsing errors or streaming responses
      const rawText = await response.text();
      let botText = "Mi dispiace, non ho ricevuto una risposta valida.";
      let parsedSuccessfully = false;
      let data;

      try {
        // 1. Attempt standard JSON parsing (e.g. valid array or object)
        data = JSON.parse(rawText);
        parsedSuccessfully = true;
      } catch (e) {
        console.warn("Standard JSON parse failed, attempting NDJSON/Stream parsing...", e);

        // 2. Fallback: Attempt to parse as NDJSON (Newline Delimited JSON) typical of streams
        try {
          const lines = rawText.split('\n').filter(line => line.trim() !== '');
          let accumulatedContent = "";
          let validStream = false;

          for (const line of lines) {
            try {
              const lineObj = JSON.parse(line);
              // Handle N8N/LangChain streaming format
              if (lineObj.type === 'item' && typeof lineObj.content === 'string') {
                accumulatedContent += lineObj.content;
                validStream = true;
              }
              // Handle generic delta/chunk format if necessary in future
            } catch (lineErr) {
              // Skip invalid lines (e.g. empty keep-aleves)
            }
          }

          if (validStream && accumulatedContent) {
            botText = accumulatedContent;
            parsedSuccessfully = false; // We handled it manually, so skip standard extraction
          } else {
            console.error("Stream parsing yielded no content");
          }
        } catch (streamErr) {
          console.error("All parsing attempts failed");
        }
      }

      // 3. If standard parsing succeeded, extract message from expected fields
      if (parsedSuccessfully && data) {
        if (Array.isArray(data) && data.length > 0) {
          botText = data[0].output || data[0].response || data[0].text || data[0].message || botText;
        } else if (typeof data === 'object') {
          botText = data.output || data.response || data.text || data.message || botText;
        }
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botText,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error("Error generating response:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "Scusami, al momento ho difficoltà a connettermi al server. Riprova tra poco o unisciti al gruppo Telegram.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Button to open chat */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-[0_0_20px_rgba(37,99,235,0.5)] transition-all duration-300 hover:scale-110 flex items-center justify-center ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
      >
        <MessageSquare size={28} />
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
        </span>
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[90vw] md:w-[400px] h-[500px] bg-[#0f1623]/95 backdrop-blur-xl border border-blue-500/30 rounded-2xl shadow-2xl flex flex-col transition-all duration-300 origin-bottom-right overflow-hidden ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-[#0f1623] p-4 flex justify-between items-center border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
              <Bot className="text-white" size={24} />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">ChartTower BOT AI</h3>
              <p className="text-xs text-green-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span> Online
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-[#0B1120]">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] p-3.5 rounded-2xl text-sm leading-relaxed shadow-md whitespace-pre-wrap ${msg.sender === 'user'
                  ? 'bg-blue-600 text-white rounded-tr-none'
                  : 'bg-[#1e293b] text-gray-200 border border-gray-700 rounded-tl-none'
                  }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-[#1e293b] border border-gray-700 p-3 rounded-2xl rounded-tl-none flex items-center gap-2">
                <Loader2 size={16} className="animate-spin text-blue-400" />
                <span className="text-xs text-gray-400">Sto scrivendo...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-[#0f1623] border-t border-gray-800">
          <div className="relative">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Chiedimi qualsiasi cosa..."
              className="w-full bg-[#151E32] text-white border border-gray-700 rounded-xl pl-4 pr-12 py-3.5 focus:outline-none focus:border-blue-500 transition-colors text-sm shadow-inner"
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors shadow-lg"
            >
              <Send size={16} />
            </button>
          </div>
          <div className="text-center mt-2">
            <span className="text-[10px] text-gray-600 flex items-center justify-center gap-1">
              <Sparkles size={8} /> Powered by ChartTower AI
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
